import { List, Typography, ListItemButton } from '@mui/material';
import { VisibilityOutlined, VisibilityOffOutlined, ErrorOutline, InfoOutlined } from '@mui/icons-material';
import { GVTypography } from 'components/lib';
import cx from 'classnames';
import { DocumentType, Layer, DocumentTypes, Separation, StoreState } from 'types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import {
  inspection,
  getSourceLayers,
  getTargetLayers,
  getTextProcessStatus,
  getSourceSeparations,
  getTargetSeparations,
  getFilesIds,
  getActivePolygonKey,
  getSourceOverprint,
  getTargetOverprint,
  getIsOCR,
} from 'store';
import { PDFManagerFactory } from 'pdftron';
import Layers from 'pdftron/core/Layers';
import Separations from 'pdftron/core/Separations';
import { decodePdfTronName } from 'utils/decodePdfTronName';
import { startShapesJob } from 'store/request/files/actions';
import PolygonInactiveIcon from 'components/icons/Shapes/PolygonInactiveIcon';
import PolygonActiveIcon from 'components/icons/Shapes/PolygonActiveIcon';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import styles from './OptionalContentList.module.css';

interface OptionalContentItemProps {
  action: Function;
  name: string;
  visible: boolean;
  rgb?: number[];
  index: number;
  test_id: string;
  documentType: DocumentType;
  itemType: 'Layer' | 'Separation';
}

const OptionalContentItem = (props: OptionalContentItemProps) => {
  const { documentType, name, itemType, index } = props;
  const dispatch = useDispatch();
  const [visibilityTooltipOpen, setVisibilityTooltipOpen] = useState(false);
  const [shapeTooltipOpen, setShapeTooltipOpen] = useState(false);

  const documentProcessed = useSelector(getTextProcessStatus);
  const conflictingAnnotations = PDFAnnotationManager.getInputAnnotations(documentType) || [];
  const overprintEnabled = useSelector(documentType === DocumentTypes.source ? getSourceOverprint : getTargetOverprint);

  const { masterFileId, sampleFileId } = useSelector(getFilesIds);
  const itemKey = `${itemType}-${index}-${name}`;
  const activePolygonKey = useSelector((state: StoreState) => getActivePolygonKey(state, documentType));
  const isActive = itemKey === activePolygonKey;

  const isNoPointerEvents = !documentProcessed || (itemType === 'Separation' && !overprintEnabled);

  const handleClick = (event: React.MouseEvent) => {
    if (props.visible === undefined || isNoPointerEvents) return;

    if (event.target instanceof HTMLElement && event.target.closest('.polygon-icon')) {
      // To prevent visibility click from triggering shape service
      return;
    }

    // Force close ALL tooltips on click
    setVisibilityTooltipOpen(false);
    setShapeTooltipOpen(false);
    props.action(props.index);
  };

  const handleVisibilityTooltip = useCallback((open: boolean) => {
    setVisibilityTooltipOpen(open);
  }, []);

  const handleShapeTooltip = useCallback((open: boolean) => {
    setShapeTooltipOpen(open);
  }, []);

  const handleSetPolygonClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    // Force close ALL tooltips on click
    setVisibilityTooltipOpen(false);
    setShapeTooltipOpen(false);

    if (isActive) {
      // If shape is already active, remove it.
      dispatch(
        inspection.actions.setPolygons({
          documentType,
          points: '',
          type: '',
          key: '',
          layerOrSeparationName: '',
        }),
      );
    } else {
      // Dispatch shape service for new shape
      dispatch(
        inspection.actions.setPolygons({
          documentType,
          points: '',
          type: itemType,
          key: itemKey,
          layerOrSeparationName: name,
        }),
      );
      // Show warning if crops exist
      if (conflictingAnnotations.length > 0) {
        dispatch(
          inspection.actions.setPendingShapeRequest({
            fileId: documentType === DocumentTypes.source ? masterFileId : sampleFileId,
            documentType,
            layerOrSeparationName: name,
            type: itemType,
          }),
        );
      } else {
        dispatch(
          startShapesJob(
            documentType === DocumentTypes.source ? masterFileId : sampleFileId,
            documentType,
            itemType,
            name,
          ),
        );
      }
    }
  };

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid={props.test_id}
      className={cx(
        styles.item,
        props.visible !== false ? styles.visible : styles.hidden,
        isNoPointerEvents && styles.frozen,
      )}
      disableRipple={isNoPointerEvents}
    >
      {props.visible === undefined ? null : (
        <>
          <span className={styles.tip}>
            <GVTooltip
              title={
                itemType === 'Separation' && !overprintEnabled
                  ? `To enable separation visibility, toggle overprint to "ON"`
                  : 'Toggle Visibility'
              }
              className={styles.tooltip}
              isOpen={visibilityTooltipOpen}
              onOpenChange={(isOpen) => handleVisibilityTooltip(isOpen)}
              disableFocusListener
              disableHoverableContent
            >
              <span className={styles.glyph}>
                {props.visible ? (
                  <VisibilityOutlined style={{ opacity: itemType === 'Separation' && !overprintEnabled ? 0.38 : 1 }} />
                ) : (
                  <VisibilityOffOutlined
                    style={{ opacity: itemType === 'Separation' && !overprintEnabled ? 0.38 : 1 }}
                  />
                )}
              </span>
            </GVTooltip>
          </span>
          <GVTooltip
            title="Set Shape"
            isOpen={shapeTooltipOpen}
            onOpenChange={(isOpen) => handleShapeTooltip(isOpen)}
            disableFocusListener
            disableHoverableContent
          >
            <span onClick={handleSetPolygonClick} className="polygon-icon">
              {isActive ? <PolygonActiveIcon /> : <PolygonInactiveIcon />}
            </span>
          </GVTooltip>
        </>
      )}

      {props.rgb && (
        <span
          style={props.rgb && { backgroundColor: `rgb(${props.rgb[0]}, ${props.rgb[1]}, ${props.rgb[2]})` }}
          className={styles.swatch}
        ></span>
      )}
      <Typography className={cx(styles.label, props.visible !== false ? styles.visible : styles.hidden)}>
        {decodePdfTronName(props.name)}
      </Typography>
    </ListItemButton>
  );
};

const OptionalContentEmpty = () => {
  const text = 'Either the file does not contain layers OR layer control is unavailable for this file type';

  return (
    <div className={styles.empty}>
      <ErrorOutline className={styles.emptyIcon} />
      <GVTypography variant="body1" color="primary" emphasis="medium">
        {text}
      </GVTypography>
    </div>
  );
};

interface OptionalContentListProps {
  documentType: DocumentType;
  separationsEnabled: boolean;
  handleSeparationsToggle: (enable: boolean, documentType: DocumentType) => void;
}

const OptionalContentList = ({
  documentType,
  separationsEnabled,
  handleSeparationsToggle,
}: OptionalContentListProps) => {
  const dispatch = useDispatch();
  const layers = useSelector(documentType === DocumentTypes.source ? getSourceLayers : getTargetLayers);
  const separations = useSelector(documentType === DocumentTypes.source ? getSourceSeparations : getTargetSeparations);
  const overprintEnabled = useSelector(documentType === DocumentTypes.source ? getSourceOverprint : getTargetOverprint);
  const isOCR = useSelector(getIsOCR);
  const isDocumentOCR = documentType === DocumentTypes.source ? isOCR.sourceIsOCR : isOCR.targetIsOCR;

  const toggleVisibility = (
    index: number,
    contentType: 'layer' | 'separation',
    childIndex?: number,
    grandchildIndex?: number,
  ) => {
    const instance = PDFManagerFactory.getViewer(documentType)?.instance;
    if (!instance) return;

    if (contentType === 'layer') {
      const { refreshLayerView } = new Layers(instance);

      const updatedLayers: Layer[] = JSON.parse(JSON.stringify(layers));

      // must check for undefined bc index value can be 0
      let layerToToggle;
      if (grandchildIndex !== undefined && childIndex !== undefined) {
        layerToToggle = updatedLayers[index].children?.[childIndex]?.children?.[grandchildIndex] as Layer;
      } else if (childIndex !== undefined) {
        layerToToggle = updatedLayers[index].children?.[childIndex] as Layer;
      } else {
        layerToToggle = updatedLayers[index];
      }
      layerToToggle = toggleLayerAndChild(layerToToggle, !layerToToggle.visible);
      dispatch(inspection.actions.setLayers({ documentType, layers: updatedLayers }));
      refreshLayerView(updatedLayers);
    } else {
      const { refreshSeparationView } = new Separations(instance);

      const updatedSeparations: Separation[] = JSON.parse(JSON.stringify(separations));
      updatedSeparations[index].enabled = !updatedSeparations[index].enabled;

      const updatedSeparation: Separation = updatedSeparations[index];

      // update visibility
      refreshSeparationView(updatedSeparation);
      dispatch(inspection.actions.setSeparations({ documentType, separations: updatedSeparations }));

      // then enable/disable seps if needed
      const separationDisabled = updatedSeparations.some((separation) => !separation.enabled);

      if (separationDisabled !== separationsEnabled) {
        handleSeparationsToggle(separationDisabled, documentType);
      }
    }
  };

  const toggleLayerAndChild = (layer: Layer, visibility: boolean) => {
    layer.visible = visibility;
    if (layer.children?.length) {
      layer.children.forEach((child) => {
        child.visible = visibility;
        toggleLayerAndChild(child, visibility);
      });
    }
    return layer;
  };

  // Effect to set all separations to visible when overprint is OFF
  useEffect(() => {
    if (!overprintEnabled && separations && separations.length > 0) {
      const disabledSeparations = separations.filter((separation) => !separation.enabled);

      if (disabledSeparations.length > 0) {
        const instance = PDFManagerFactory.getViewer(documentType)?.instance;
        if (!instance) return;

        const { refreshSeparationView } = new Separations(instance);
        const updatedSeparations: Separation[] = JSON.parse(JSON.stringify(separations));

        // Enable all disabled separations
        updatedSeparations.forEach((separation) => {
          if (!separation.enabled) {
            separation.enabled = true;
            refreshSeparationView(separation);
          }
        });
        dispatch(inspection.actions.setSeparations({ documentType, separations: updatedSeparations }));

        // Update global separation state
        const separationDisabled = updatedSeparations.some((separation) => !separation.enabled);
        if (separationDisabled !== separationsEnabled) {
          handleSeparationsToggle(separationDisabled, documentType);
        }
      }
    }
  }, [overprintEnabled, separations, separationsEnabled, documentType]);

  const renderLayers = (layer: Layer, index: number) => {
    if (layer.children) {
      return (
        <div key={`${layer.name} ${index}`}>
          <OptionalContentItem
            action={() => toggleVisibility(index, 'layer')}
            name={layer.name}
            visible={layer.visible}
            index={index}
            test_id="layer_name"
            documentType={documentType}
            itemType={'Layer'}
          />
          {layer.children.map(renderChildLayers(index))}
        </div>
      );
    } else {
      return (
        <OptionalContentItem
          action={() => toggleVisibility(index, 'layer')}
          name={layer.name}
          visible={layer.visible}
          index={index}
          key={`${layer.name} ${index}`}
          test_id="layer_name"
          documentType={documentType}
          itemType={'Layer'}
        />
      );
    }
  };

  const renderChildLayers = (layerIndex: number) => (child: Layer, childIndex: number) => {
    if (child.children) {
      return (
        <div key={`${child.name} ${childIndex}`}>
          <div className={styles.child}>
            <OptionalContentItem
              action={() => toggleVisibility(layerIndex, 'layer', childIndex)}
              name={child.name}
              visible={child.visible}
              index={childIndex}
              test_id="layer_child_name"
              documentType={documentType}
              itemType={'Layer'}
            />
          </div>
          {child.children.map(renderGrandchildLayers(layerIndex, childIndex))}
        </div>
      );
    } else {
      return (
        <div className={styles.child} key={`${child.name} ${childIndex}`}>
          <OptionalContentItem
            action={() => toggleVisibility(layerIndex, 'layer', childIndex)}
            name={child.name}
            visible={child.visible}
            index={childIndex}
            test_id="layer_child_name"
            documentType={documentType}
            itemType={'Layer'}
          />
        </div>
      );
    }
  };

  // Unlikely but CTF supports so we will support
  const renderGrandchildLayers =
    (layerIndex: number, childIndex: number) => (grandChild: Layer, grandChildIndex: number) => (
      <div className={styles.grandchild} key={`${grandChild.name} ${grandChildIndex}`}>
        <OptionalContentItem
          action={() => toggleVisibility(layerIndex, 'layer', childIndex, grandChildIndex)}
          name={grandChild.name}
          visible={grandChild.visible}
          index={grandChildIndex}
          test_id="layer_grandchild"
          documentType={documentType}
          itemType={'Layer'}
        />
      </div>
    );

  return (
    <div className={styles.column}>
      {/* if both layers and separations are missing from the document, display OptionalContentEmpty */}
      {(!layers || layers?.length === 0) && (!separations || separations?.length === 0) && <OptionalContentEmpty />}

      {layers && layers.length > 0 && (
        <>
          <Typography variant="caption" className={styles.title}>
            LAYERS
          </Typography>
          <List className={styles.list}>{layers.map(renderLayers)}</List>
        </>
      )}

      {separations && separations.length > 0 && (
        <>
          <div className={styles.heading}>
            <Typography variant="caption" className={styles.title}>
              SEPARATIONS
            </Typography>
            {isDocumentOCR && (
              <GVTooltip
                title="Only CMYK separations are available post-OCR"
                side="top"
                disableFocusListener
                disableHoverableContent
              >
                <InfoOutlined className={styles.info} />
              </GVTooltip>
            )}
          </div>
          <List className={styles.list}>
            {separations.map((separation, index) => {
              return (
                <OptionalContentItem
                  action={() => toggleVisibility(index, 'separation')}
                  name={separation.name}
                  test_id="separation_name"
                  visible={separation.enabled}
                  rgb={separation.rgb}
                  index={index}
                  // eslint-disable-next-line
                  key={`${separation.name} ${index}`}
                  documentType={documentType}
                  itemType={'Separation'}
                />
              );
            })}
          </List>
        </>
      )}
    </div>
  );
};

export default OptionalContentList;
